//@flow
import type { ThunkAction, ID } from '../core/types'
import {
	startCommunication,
	endCommunication,
	isCommInProgress
} from '../modules/communication'
import { lreplace, lempty } from '../modules/list'
import { hset, hincrby } from '../modules/hash'
import { mergeEntities } from '../modules/entities'
import { normalize } from 'normalizr'
import i18n from '../core/i18n'
import numeral from '../core/numeral'
import moment from '../core/moment'
import { schemas, domains } from './constants'
import { services } from '../core/feathers'
import { hasMoreEntitiesToFetch } from './selectors'
import { fetchSession } from './session'
import { batch } from '../modules/lib/batchReducer'
import { PAGE_SIZE, MIN_OFFSET, NOTIFICATION_HIDE_DELAY } from '../config'
import { getActiveEntities, getCurrentPage, getActiveEntity } from './selectors'
import { getSearchItemsResource, getSearchAuctionsResource } from './utils'

function createRelationsForDomainParams(domain: string) {
	switch (domain) {
		case domains.AUCTIONS:
			return {
				$eager: '[charity]'
			}
		case domains.ITEMS:
			return {
				$eager: '[auction.charity, bids, currentBid, media]'
			}
		case domains.CHARITIES:
			return {}
		default:
			return {}
	}
}

export function searchItems(
	auctionId: ?ID,
	charityId: ?ID,
	searchParams: Object = {}
): ThunkAction {
	return async function(dispatch, getState) {
		const resource = getSearchItemsResource(auctionId, charityId)
		if (isCommInProgress(resource, getState())) {
			console.log('searchItems called but another one in progress')
			return
		}
		let items = []
		const page = getCurrentPage(resource, getState())
		let requestActions = []
		if (page === 0) {
			requestActions.push(clearActiveEntities(resource))
		}
		dispatch(
			batch(
				'SEARCH_ITEMS_REQUEST',
				startCommunication(resource),
				...requestActions
			)
		)

		const {
			hash: { ui }
		} = getState()
		let category = ui ? ui.currentFilter || '' : ''

		try {
			items = await services.searchResultsItems.find({
				query: {
					auctionId: auctionId,
					charityId: charityId,
					category: category,
					$skip: page * PAGE_SIZE,
					$limit: PAGE_SIZE
				}
			})
		} catch (error) {
			console.warn('fetching items failed', error)
			dispatch(
				batch('SEARCH_ITEMS_END_WITH_ERROR', endCommunication(resource, false))
			)
		}
		if (!items.length) {
			dispatch(
				batch(
					'SEARCH_ITEMS_END_WITH_NO_DATA',
					setHasMore(resource, false),
					endCommunication(resource, true)
				)
			)
		} else {
			const { entities, result } = normalize(items, [schemas.items])
			const activeId = getActiveEntity(resource)(getState())
			const hasMore = Boolean(result.length === PAGE_SIZE)
			const previousActiveEntities = getActiveEntities(resource, getState())
			const activeEntities = Array.from(
				new Set([...previousActiveEntities, ...result])
			)
			const actions = [
				mergeEntities(entities),
				setActiveEntities(resource, activeEntities),
				setHasMore(resource, hasMore),
				endCommunication(resource)
			]
			if (!activeId) actions.push(setActiveEntity(resource, result[0]))
			dispatch(batch('SEARCH_ITEMS_SUCCESS', ...actions))
		}
	}
}

export function searchAuctions(
	charityId: ?ID,
	searchParams: Object = {}
): ThunkAction {
	return async function(dispatch, getState) {
		const resource = getSearchAuctionsResource(charityId)
		if (isCommInProgress(resource, getState())) {
			console.log('searchAuctions called but another one in progress')
			return
		}
		let auctions = []
		const page = getCurrentPage(resource, getState())
		let requestActions = []
		if (page === 0) {
			requestActions.push(clearActiveEntities(resource))
		}
		dispatch(
			batch(
				'SEARCH_AUCTIONS_REQUEST',
				startCommunication(resource),
				...requestActions
			)
		)
		try {
			auctions = await services.searchResultsAuctions.find({
				query: {
					charityId: charityId,
					$skip: page * PAGE_SIZE,
					$limit: PAGE_SIZE
				}
			})
		} catch (error) {
			console.warn('fetching auctions failed', error)
			dispatch(
				batch(
					'SEARCH_AUCTIONS_END_WITH_ERROR',
					endCommunication(resource, false)
				)
			)
		}
		if (!auctions.length) {
			dispatch(
				batch(
					'SEARCH_AUCTIONS_END_WITH_NO_DATA',
					setHasMore(resource, false),
					endCommunication(resource, true)
				)
			)
		} else {
			const { entities, result } = normalize(auctions, [schemas.auctions])
			const activeId = getActiveEntity(resource)(getState())
			const hasMore = Boolean(result.length === PAGE_SIZE)
			const previousActiveEntities = getActiveEntities(resource, getState())
			const activeEntities = Array.from(
				new Set([...previousActiveEntities, ...result])
			)
			const actions = [
				mergeEntities(entities),
				setActiveEntities(resource, activeEntities),
				setHasMore(resource, hasMore),
				endCommunication(resource)
			]
			if (!activeId) actions.push(setActiveEntity(resource, result[0]))
			dispatch(batch('SEARCH_AUCTIONS_SUCCESS', ...actions))
		}
	}
}

export function searchCharities(searchParams: Object = {}): ThunkAction {
	return async function(dispatch, getState) {
		const resource = domains.CHARITIES
		if (isCommInProgress(resource, getState())) {
			console.log('searchCharities called but another one in progress')
			return
		}
		let charities = []
		const page = getCurrentPage(resource, getState())
		let requestActions = []
		if (page === 0) {
			requestActions.push(clearActiveEntities(resource))
		}
		dispatch(
			batch(
				'SEARCH_CHARITIES_REQUEST',
				startCommunication(resource),
				...requestActions
			)
		)
		try {
			const query = {
				...searchParams,
				published: true,
				$skip: page * PAGE_SIZE,
				$limit: PAGE_SIZE
			}
			charities = await services.charities.find({ query })
		} catch (error) {
			console.warn('fetching charities failed', error)
			dispatch(
				batch(
					'SEARCH_CHARITIES_END_WITH_ERROR',
					endCommunication(resource, false)
				)
			)
		}
		if (!charities.length) {
			dispatch(
				batch(
					'SEARCH_CHARITIES_END_WITH_NO_DATA',
					setHasMore(resource, false),
					endCommunication(resource, true)
				)
			)
		} else {
			const { entities, result } = normalize(charities, [schemas.charities])
			const activeId = getActiveEntity(resource)(getState())
			const hasMore = Boolean(result.length === PAGE_SIZE)
			const previousActiveEntities = getActiveEntities(resource, getState())
			const activeEntities = Array.from(
				new Set([...previousActiveEntities, ...result])
			)
			const actions = [
				mergeEntities(entities),
				setActiveEntities(resource, activeEntities),
				setHasMore(resource, hasMore),
				endCommunication(resource)
			]
			if (!activeId) actions.push(setActiveEntity(resource, result[0]))
			dispatch(batch('SEARCH_CHARITIES_SUCCESS', ...actions))
		}
	}
}

export function findEntity(domain: string, id: string): ThunkAction {
	return async function(dispatch, getState) {
		const commResource = `${domain}:${id}`
		if (isCommInProgress(commResource, getState())) {
			console.log(
				'findEntity called but another one in progress for:',
				commResource
			)
			return
		}
		dispatch(batch('FIND_ENTITY_REQUEST', startCommunication(commResource)))
		try {
			const params = createRelationsForDomainParams(domain)
			const entity = await services[domain].get(id, { query: params })
			const { entities, result } = normalize(entity, schemas[domain])
			dispatch(
				batch(
					'FIND_ENTITY_SUCCESS',
					mergeEntities(entities),
					setActiveEntity(domain, result),
					endCommunication(commResource, true)
				)
			)
		} catch (e) {
			console.warn('fetching entity failed', e)
			dispatch(
				batch('FIND_ENTITY_FAILURE', endCommunication(commResource, false))
			)
		}
	}
}

export const checkIfMoreEntitiesNeeded = (
	domain: string,
	id: number,
	auctionId: ?ID,
	charityId: ?ID
): ThunkAction => (dispatch, getState) => {
	const resource = getSearchItemsResource(auctionId, charityId)
	const actives = getActiveEntities(resource, getState())
	const hasToCheck = actives.indexOf(id) >= actives.length - MIN_OFFSET
	if (!hasToCheck) return
	const hasMoreEntities = hasMoreEntitiesToFetch(resource, getState())
	if (!hasMoreEntities) return
	dispatch(nextPage(domain, auctionId, charityId))
}

export const nextPage = (
	domain: string,
	auctionId: ?ID,
	charityId: ?ID
): ThunkAction => (dispatch, getState) => {
	const resource = getSearchItemsResource(auctionId, charityId)
	dispatch(incrementCurrentPage(resource))
	if (domain === domains.ITEMS) {
		dispatch(searchItems(auctionId, charityId))
	} else if (domain === domains.AUCTIONS) {
		dispatch(searchAuctions(charityId))
	} else {
		dispatch(searchCharities())
	}
}
/* There is no garbage page collector at the moment, so by now is not needed a
fetch for previous pages
const page = hget(domain, 'currentPage', state) || 0
if (actives.indexOf(id) < MIN_OFFSET && page !== 0) {
  pageDown(domain, dispatch)
}*/

const incrementCurrentPage = (domain: string) => hincrby(domain, 'currentPage')

export const setActiveEntity = (domain: string, id: number) =>
	hset(domain, 'activeId', id)

export const setActiveEntities = (domain: string, values: Array<ID>) =>
	lreplace(domain, values)
// values.map(v => rpush(domain, v))

export const clearActiveEntities = (domain: string) => lempty(domain)

export const setHasMore = (domain: string, value: boolean) =>
	hset(domain, 'hasMore', value)

export const setNotification = (value: any): ThunkAction => dispatch => {
	dispatch(hset(domains.SESSION, 'notification', value))
	setTimeout(
		() => dispatch(hset(domains.SESSION, 'notification', false)),
		NOTIFICATION_HIDE_DELAY
	)
}

export const setPaymentMethod = (instance: Object): ThunkAction =>
	async function(dispatch) {
		try {
			const payload = await instance.requestPaymentMethod()
			await services.paymentMethod.create(payload)
			dispatch(fetchSession())
			return payload
		} catch (e) {
			console.warn('setPaymentMethod failed', e)
			return false
		}
	}

export const changeLocales = (locale: string) => {
	if (!locale) return
	i18n.changeLanguage(locale)
	numeral.locale(locale)
	moment.locale(locale)
}



// WEBPACK FOOTER //
// ./src/services/actions.js